import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FaRegBell,
    FaCheck,
    FaUtensils,
    FaBoxOpen,
    FaMotorcycle,
    FaHandHolding,
    FaConciergeBell,
    FaCheckDouble,
    FaTimesCircle,
    FaCircle,
    FaPlus,
} from 'react-icons/fa';
import { getStatusMeta } from './ui/orderStatusFlow';

const STATUS_ICON_BY_NAME = {
    new: FaRegBell,
    accepted: FaCheck,
    cooking: FaUtensils,
    ready: FaBoxOpen,
    delivery: FaMotorcycle,
    pickup: FaHandHolding,
    served: FaConciergeBell,
    done: FaCheckDouble,
    cancelled: FaTimesCircle,
    unknown: FaCircle,
};

function getStatusIcon(statusLabel) {
    const meta = getStatusMeta(statusLabel);
    return STATUS_ICON_BY_NAME[meta.icon] || FaCircle;
}

function StatusFilterOption({ opt, onSelect, selected = false }) {
    const meta = getStatusMeta(opt.label);
    const Icon = getStatusIcon(opt.label);

    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            className={`rich-dropdown__option${selected ? ' rich-dropdown__option--selected' : ''}`}
            onClick={() => onSelect(opt.code)}
        >
            <span
                className="rich-dropdown__option-icon"
                style={{ color: meta.color, backgroundColor: meta.bg }}
            >
                <Icon aria-hidden="true" />
            </span>
            <span className="rich-dropdown__option-body">
                <span className="rich-dropdown__option-label">{meta.label}</span>
                <span className="rich-dropdown__option-hint">
                    {selected ? 'Tap to remove filter' : 'Tap to add filter'}
                </span>
            </span>
            <span className="rich-dropdown__option-mark order-status-filter__option-add" aria-hidden="true">
                {selected ? <FaCheck /> : <FaPlus />}
            </span>
        </button>
    );
}

function StatusFilterChip({ opt, onRemove }) {
    const meta = getStatusMeta(opt.label);
    const Icon = getStatusIcon(opt.label);

    return (
        <span
            className="order-status-filter__chip"
            style={{
                color: meta.color,
                backgroundColor: meta.bg,
                borderColor: meta.color,
            }}
        >
            <Icon className="order-status-filter__chip-icon" aria-hidden="true" />
            <span className="order-status-filter__chip-label">{meta.label}</span>
            <button
                type="button"
                className="order-status-filter__chip-remove"
                onClick={onRemove}
                aria-label={`Remove ${meta.label}`}
            >
                ×
            </button>
        </span>
    );
}

export function FilterDropdown({ label, panelId, children, active = false, onOpenChange }) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyle, setPanelStyle] = useState({
        position: 'fixed',
        top: 0,
        left: 0,
        width: 240,
        zIndex: 10050,
        visibility: 'hidden',
    });

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pad = 8;
        const minW = Math.max(Math.round(r.width), 220);
        let left = r.left;
        if (left + minW > window.innerWidth - pad) {
            left = Math.max(pad, window.innerWidth - minW - pad);
        }
        const maxPanelH = 280;
        let top = r.bottom + 4;
        if (top + maxPanelH > window.innerHeight - pad) {
            top = Math.max(pad, r.top - maxPanelH - 4);
        }
        setPanelStyle({
            position: 'fixed',
            left,
            top,
            width: minW,
            zIndex: 10050,
            visibility: 'visible',
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const onResizeOrScroll = () => updatePosition();
        window.addEventListener('resize', onResizeOrScroll);
        window.addEventListener('scroll', onResizeOrScroll, true);
        return () => {
            window.removeEventListener('resize', onResizeOrScroll);
            window.removeEventListener('scroll', onResizeOrScroll, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const t = e.target;
            if (!(t instanceof Node)) return;
            if (triggerRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    useEffect(() => {
        onOpenChange?.(open);
    }, [open, onOpenChange]);

    const panel =
        open &&
        createPortal(
            <div
                ref={panelRef}
                id={panelId}
                className="jreats-order-filter__panel jreats-order-filter__panel--floating"
                style={panelStyle}
                role="listbox"
            >
                {children}
            </div>,
            document.body
        );

    return (
        <div className="jreats-order-filter">
            <button
                type="button"
                ref={triggerRef}
                className={`jreats-order-filter__trigger${open ? ' jreats-order-filter__trigger--open' : ''}${active ? ' jreats-order-filter__trigger--active' : ''}`}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="jreats-order-filter__label">{label}</span>
            </button>
            {panel}
        </div>
    );
}

export default function OrderStatusFilterDropdown({
    panelId,
    statusOptions,
    statusSelections,
    onToggleStatus,
    placeholder = 'Search statuses...',
}) {
    const [open, setOpen] = useState(false);
    const [statusSearch, setStatusSearch] = useState('');
    const searchWrapRef = useRef(null);
    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyle, setPanelStyle] = useState({
        position: 'fixed',
        top: 0,
        left: 0,
        width: 200,
        zIndex: 10050,
        visibility: 'hidden',
    });

    const selectedOpts = statusOptions.filter((opt) => statusSelections[opt.code]);
    const searchNorm = statusSearch.trim().toLowerCase();
    const visibleOptions = statusOptions.filter(
        (opt) => !searchNorm || opt.label.toLowerCase().includes(searchNorm)
    );

    const updatePosition = useCallback(() => {
        const el = searchWrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pad = 8;
        const width = Math.max(Math.round(r.width), 200);
        let left = r.left;
        if (left + width > window.innerWidth - pad) {
            left = Math.max(pad, window.innerWidth - width - pad);
        }
        const maxPanelH = 280;
        let top = r.bottom + 4;
        if (top + maxPanelH > window.innerHeight - pad) {
            top = Math.max(pad, r.top - maxPanelH - 4);
        }
        setPanelStyle({
            position: 'fixed',
            left,
            top,
            width,
            zIndex: 10050,
            visibility: 'visible',
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const onResizeOrScroll = () => updatePosition();
        window.addEventListener('resize', onResizeOrScroll);
        window.addEventListener('scroll', onResizeOrScroll, true);
        return () => {
            window.removeEventListener('resize', onResizeOrScroll);
            window.removeEventListener('scroll', onResizeOrScroll, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const t = e.target;
            if (!(t instanceof Node)) return;
            if (searchWrapRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
            setStatusSearch('');
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setStatusSearch('');
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const handleSelect = (code) => {
        onToggleStatus(code);
        setStatusSearch('');
        inputRef.current?.focus();
    };

    const panel =
        open &&
        createPortal(
            <div
                ref={panelRef}
                id={panelId}
                className="jreats-order-filter__panel jreats-order-filter__panel--floating rich-dropdown__panel order-status-filter__panel"
                style={panelStyle}
                role="listbox"
                aria-label="Available statuses"
            >
                {visibleOptions.length > 0 ? (
                    <>
                        <div className="rich-dropdown__panel-header">
                            <span className="rich-dropdown__panel-title">Manage statuses</span>
                            <span className="rich-dropdown__panel-count">
                                {selectedOpts.length} selected
                            </span>
                        </div>
                        <div className="rich-dropdown__panel-body">
                            {visibleOptions.map((opt) => (
                                <StatusFilterOption
                                    key={opt.code}
                                    opt={opt}
                                    selected={Boolean(statusSelections[opt.code])}
                                    onSelect={handleSelect}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="jreats-order-filter__empty">
                        No statuses found
                    </p>
                )}
            </div>,
            document.body
        );

    return (
        <div className="order-status-filter">
            <div
                className={`order-status-filter__search-box${open ? ' order-status-filter__search-box--open' : ''}`}
                ref={searchWrapRef}
            >
                {selectedOpts.length > 0 ? (
                    <div
                        className="order-status-filter__inline-chips"
                        aria-label="Selected statuses"
                    >
                        <span className="order-status-filter__inline-status">
                            {getStatusMeta(selectedOpts[0].label).label}
                        </span>
                        {selectedOpts.length > 1 ? (
                            <span
                                className="order-status-filter__inline-count"
                                aria-label={`${selectedOpts.length - 1} more statuses selected`}
                            >
                                +{selectedOpts.length - 1}
                            </span>
                        ) : null}
                    </div>
                ) : null}
                <input
                    ref={inputRef}
                    type="search"
                    className="order-status-filter__search-input"
                    placeholder={selectedOpts.length > 0 ? '' : placeholder}
                    value={statusSearch}
                    onChange={(e) => {
                        setStatusSearch(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    aria-label="Search and add statuses"
                    autoComplete="off"
                />
            </div>
            {panel}
            {selectedOpts.length > 0 ? (
                <div
                    className="order-status-filter__chips order-status-filter__chips--external"
                    aria-label="Selected statuses"
                >
                    {selectedOpts.map((opt) => (
                        <StatusFilterChip
                            key={opt.code}
                            opt={opt}
                            onRemove={() => onToggleStatus(opt.code)}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
